import React, { useEffect, useState } from 'react';
import {
  Breakpoint,
  getBreakpoints,
  isBreakpointSmallOrBelow,
  useBreakpoint,
} from '@noths/polaris-client-ribbons-base';
import type { DrawerProps } from '@noths/polaris-client-ribbons-design-system';
import { Drawer, DrawerHeader } from '@noths/polaris-client-ribbons-design-system';
import { Button } from '@noths/polaris-design-system-core-components';

import { FacetHeader } from 'src/components/atoms/FacetHeader/FacetHeader';
import { ActiveFacetsContainer } from 'src/components/organisms/ActiveFacets/ActiveFacetsContainer';
import { ListingsControlsContainer } from 'src/components/organisms/ListingsControls/ListingsControlsContainer';
import * as styles from 'src/components/organisms/ListingsControlsDrawer/ListingsControlsDrawer.styles';
import { MODAL_HEADING } from './copy';

const breakpointsWithDrawer = getBreakpoints({ maxBreakpoint: Breakpoint.M });

const APPLIED_FILTERS_TITLE = 'Applied Filters';

export interface ListingsControlsDrawerStateProps {
  drawerCTAButtonDisabled: boolean;
  drawerCTAButtonText: string;
  drawerOpen: boolean;
  loading: boolean;
  totalActiveFacets: number;
}

export interface ListingsControlsDispatchProps {
  closeDrawer: () => void;
  onDrawerCTAButtonClick: () => void;
}

type ListingsControlsDrawerProps = ListingsControlsDrawerStateProps & ListingsControlsDispatchProps;

export const ListingsControlsDrawer = ({
  closeDrawer,
  drawerCTAButtonDisabled,
  drawerCTAButtonText,
  drawerOpen,
  loading,
  onDrawerCTAButtonClick,
  totalActiveFacets,
}: ListingsControlsDrawerProps) => {
  const breakpoint = useBreakpoint({ initializeWithLarge: true });
  const isMobile = isBreakpointSmallOrBelow(breakpoint);
  const [drawerSize, setDrawerSize] = useState<DrawerProps['panelSize']>('full');

  const handleDrawerClose = () => {
    closeDrawer();
  };

  const handleViewResults = () => {
    onDrawerCTAButtonClick();
  };

  useEffect(() => {
    if (!breakpointsWithDrawer.includes(breakpoint)) {
      closeDrawer();
    }

    setDrawerSize(isMobile ? 'full' : 'partial');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpoint]);

  return (
    <Drawer
      contentContainerStyles={styles.drawerRoot}
      contentLabel="Product filtering options"
      isOpen={drawerOpen}
      onOverlayClick={handleDrawerClose}
      onRequestClose={handleDrawerClose}
      panelSize={drawerSize}
      transitionFrom="left"
    >
      <div css={styles.drawerHeader}>
        <DrawerHeader headingLevel="h2" onClose={handleDrawerClose} title={MODAL_HEADING} />
      </div>
      <div css={styles.drawerBody}>
        {totalActiveFacets > 0 && (
          <div css={styles.activeFacetsWrapper}>
            <FacetHeader heading={APPLIED_FILTERS_TITLE} />
            <ActiveFacetsContainer />
          </div>
        )}
        <ListingsControlsContainer />
      </div>
      <div css={styles.drawerFooter}>
        <Button disabled={drawerCTAButtonDisabled} onClick={handleViewResults}>
          {loading ? 'Updating...' : drawerCTAButtonText}
        </Button>
      </div>
    </Drawer>
  );
};
